import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import TranslateIcon from '@mui/icons-material/Translate';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';
import {
  getLanguageSettings,
  saveLanguageSettings,
  getTranslationOverrides,
  saveTranslationOverrides,
  machineTranslateTexts,
  type LanguageSettingsData,
  type StorefrontLanguageItem,
} from '../../../services/ecommerceApi';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';

const FALLBACK_CATALOG: StorefrontLanguageItem[] = [
  { code: 'tr', name: 'Türkçe', nativeName: 'Türkçe', flag: '🇹🇷', isActive: true, isDefault: true, isRtl: false, displayOrder: 0 },
  { code: 'en', name: 'İngilizce', nativeName: 'English', flag: '🇺🇸', isActive: false, isDefault: false, isRtl: false, displayOrder: 1 },
  { code: 'fr', name: 'Fransızca', nativeName: 'Français', flag: '🇫🇷', isActive: false, isDefault: false, isRtl: false, displayOrder: 2 },
  { code: 'ar', name: 'Arapça', nativeName: 'العربية', flag: '🇸🇦', isActive: false, isDefault: false, isRtl: true, displayOrder: 3 },
  { code: 'ru', name: 'Rusça', nativeName: 'Русский', flag: '🇷🇺', isActive: false, isDefault: false, isRtl: false, displayOrder: 4 },
  { code: 'de', name: 'Almanca', nativeName: 'Deutsch', flag: '🇩🇪', isActive: false, isDefault: false, isRtl: false, displayOrder: 5 },
  { code: 'es', name: 'İspanyolca', nativeName: 'Español', flag: '🇪🇸', isActive: false, isDefault: false, isRtl: false, displayOrder: 6 },
  { code: 'it', name: 'İtalyanca', nativeName: 'Italiano', flag: '🇮🇹', isActive: false, isDefault: false, isRtl: false, displayOrder: 7 },
  { code: 'nl', name: 'Hollandaca', nativeName: 'Nederlands', flag: '🇳🇱', isActive: false, isDefault: false, isRtl: false, displayOrder: 8 },
  { code: 'az', name: 'Azerice', nativeName: 'Azərbaycan', flag: '🇦🇿', isActive: false, isDefault: false, isRtl: false, displayOrder: 9 },
  { code: 'fa', name: 'Farsça', nativeName: 'فارسی', flag: '🇮🇷', isActive: false, isDefault: false, isRtl: true, displayOrder: 10 },
  { code: 'zh', name: 'Çince', nativeName: '中文', flag: '🇨🇳', isActive: false, isDefault: false, isRtl: false, displayOrder: 11 },
];

const DEFAULT_COMPANY_LANGUAGES: StorefrontLanguageItem[] = FALLBACK_CATALOG.filter((item) => item.code === 'tr');

const WIN1254_REVERSE: Record<string, number> = {
  '€': 0x80, '‚': 0x82, 'ƒ': 0x83, '„': 0x84, '…': 0x85, '†': 0x86, '‡': 0x87,
  'ˆ': 0x88, '‰': 0x89, 'Š': 0x8A, '‹': 0x8B, 'Œ': 0x8C, 'Ž': 0x8E,
  '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97,
  '˜': 0x98, '™': 0x99, 'š': 0x9A, '›': 0x9B, 'œ': 0x9C, 'ž': 0x9E, 'Ÿ': 0x9F,
  'Ğ': 0xD0, 'İ': 0xDD, 'Ş': 0xDE, 'ğ': 0xF0, 'ı': 0xFD, 'ş': 0xFE,
};

function looksLikeMojibake(value?: string): boolean {
  return !!value && /Ã.|Ä.|Å.|Â.|ğŸ|ðŸ/.test(value);
}

function repairMojibake(value?: string): string {
  if (!value || !looksLikeMojibake(value)) return value || '';
  const bytes = new Uint8Array(value.length);
  for (let i = 0; i < value.length; i += 1) {
    const mapped = WIN1254_REVERSE[value[i]];
    const code = mapped ?? value.charCodeAt(i);
    if (code > 255) return value;
    bytes[i] = code;
  }
  const repaired = new TextDecoder('utf-8').decode(bytes);
  return repaired.includes('\uFFFD') ? value : repaired;
}

function pickDisplay(...candidates: Array<string | undefined>): string {
  let repaired = '';
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (!trimmed) continue;
    if (!looksLikeMojibake(trimmed)) return trimmed;
    if (!repaired) repaired = repairMojibake(trimmed);
  }
  return repaired;
}

function hydrateLanguage(
  item: StorefrontLanguageItem,
  catalogByCode: Map<string, StorefrontLanguageItem>
): StorefrontLanguageItem {
  const catalog = catalogByCode.get(item.code);
  return {
    ...item,
    name: pickDisplay(catalog?.name, item.name) || item.code.toUpperCase(),
    nativeName: pickDisplay(catalog?.nativeName, item.nativeName, item.name) || item.code,
    flag: pickDisplay(catalog?.flag, item.flag) || '🏳️',
    isRtl: item.isRtl || !!catalog?.isRtl,
  };
}

function buildCatalogMap(extra?: StorefrontLanguageItem[]): Map<string, StorefrontLanguageItem> {
  return new Map(
    [...FALLBACK_CATALOG, ...(extra || [])]
      .filter((item) => item?.code)
      .map((item) => [item.code, item])
  );
}

const emptyCustom = {
  code: '',
  name: '',
  nativeName: '',
  flag: '',
  isRtl: false,
};

interface LanguageSettingsProps {
  companyId: string | null;
}

const LanguageSettings: React.FC<LanguageSettingsProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<LanguageSettingsData>({
    showSwitcher: false,
    defaultLanguage: 'tr',
    languages: DEFAULT_COMPANY_LANGUAGES,
  });
  const [catalog, setCatalog] = useState<StorefrontLanguageItem[]>(FALLBACK_CATALOG);
  const [custom, setCustom] = useState(emptyCustom);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedRef = useRef(false);

  // Manuel çeviriler (override) bölümü durumu
  type OverrideRow = { id: string; source: string; values: Record<string, string> };
  const [overrideRows, setOverrideRows] = useState<OverrideRow[]>([]);
  const [overridesSaving, setOverridesSaving] = useState(false);
  const [prefillingId, setPrefillingId] = useState<string | null>(null);
  const [newSource, setNewSource] = useState('');
  const overridesLoadedRef = useRef(false);
  const overridesSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const response = await getLanguageSettings(companyId);
        const catalogByCode = buildCatalogMap(response?.catalog);
        setCatalog(Array.from(catalogByCode.values()).map((item) => hydrateLanguage(item, catalogByCode)));
        const rawLanguages =
          Array.isArray(response?.data?.languages) && response.data.languages.length > 0
            ? response.data.languages
            : DEFAULT_COMPANY_LANGUAGES;
        const languages = rawLanguages.map((lang) => hydrateLanguage(lang, catalogByCode));
        const next: LanguageSettingsData = {
          showSwitcher: !!response?.data?.showSwitcher,
          defaultLanguage: response?.data?.defaultLanguage || 'tr',
          languages,
        };
        setSettings(next);
        loadedRef.current = true;
        if (rawLanguages.some((lang) => looksLikeMojibake(lang.name) || looksLikeMojibake(lang.nativeName) || looksLikeMojibake(lang.flag))) {
          try {
            const saved = await saveLanguageSettings(next, companyId);
            if (saved?.data) {
              setSettings({
                showSwitcher: !!saved.data.showSwitcher,
                defaultLanguage: saved.data.defaultLanguage || next.defaultLanguage,
                languages: (saved.data.languages || next.languages).map((lang) =>
                  hydrateLanguage(lang, catalogByCode)
                ),
              });
            }
          } catch (healError) {
            console.error('Bozuk dil metinleri kaydedilemedi:', healError);
          }
        }
      } catch (error) {
        console.error('Dil ayarları yüklenemedi:', error);
        setFeedback({ type: 'error', message: t('ecommerceLanguages.loadError') });
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [companyId, t]);

  const persist = async (next: LanguageSettingsData) => {
    if (!companyId) return;
    setSaving(true);
    setFeedback(null);
    try {
      const response = await saveLanguageSettings(next, companyId);
      if (response?.data) {
        const catalogByCode = buildCatalogMap(catalog);
        setSettings({
          showSwitcher: !!response.data.showSwitcher,
          defaultLanguage: response.data.defaultLanguage || next.defaultLanguage,
          languages: (response.data.languages || next.languages).map((lang) =>
            hydrateLanguage(lang, catalogByCode)
          ),
        });
      }
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
    } catch (error) {
      console.error('Dil ayarları kaydedilemedi:', error);
      setFeedback({ type: 'error', message: t('ecommerceLanguages.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const schedulePersist = (next: LanguageSettingsData) => {
    if (!loadedRef.current || !companyId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persist(next), 400);
  };

  const updateSettings = (next: LanguageSettingsData) => {
    setSettings(next);
    schedulePersist(next);
  };

  const activeLanguages = useMemo(
    () => settings.languages.filter((lang) => lang.isActive),
    [settings.languages]
  );

  const unusedCatalog = useMemo(
    () => catalog.filter((item) => !settings.languages.some((lang) => lang.code === item.code)),
    [catalog, settings.languages]
  );

  const handleToggleActive = (code: string, checked: boolean) => {
    const languages = settings.languages.map((lang) => {
      if (lang.code !== code) return lang;
      return { ...lang, isActive: checked, isDefault: checked ? lang.isDefault : false };
    });
    const stillDefault = languages.some((lang) => lang.isActive && lang.isDefault);
    if (!stillDefault) {
      const firstActive = languages.find((lang) => lang.isActive);
      if (firstActive) firstActive.isDefault = true;
    }
    const defaultLanguage = languages.find((lang) => lang.isDefault)?.code || settings.defaultLanguage;
    const activeCount = languages.filter((lang) => lang.isActive).length;
    updateSettings({
      ...settings,
      defaultLanguage,
      languages,
      // İkinci aktif dilde seçiciyi otomatik aç (döviz seçiciyle aynı davranış).
      showSwitcher: activeCount >= 2,
    });
  };

  const handleSetDefault = (code: string) => {
    const languages = settings.languages.map((lang) => ({
      ...lang,
      isDefault: lang.code === code,
      isActive: lang.code === code ? true : lang.isActive,
    }));
    updateSettings({
      ...settings,
      defaultLanguage: code,
      languages,
      showSwitcher: languages.filter((lang) => lang.isActive).length >= 2,
    });
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= settings.languages.length) return;
    const languages = settings.languages.slice();
    const [item] = languages.splice(index, 1);
    languages.splice(target, 0, item);
    updateSettings({
      ...settings,
      languages: languages.map((lang, displayOrder) => ({ ...lang, displayOrder })),
    });
  };

  const handleRemove = (code: string) => {
    const languages = settings.languages.filter((lang) => lang.code !== code);
    if (languages.length === 0) return;
    if (!languages.some((lang) => lang.isActive)) {
      languages[0].isActive = true;
    }
    if (!languages.some((lang) => lang.isDefault && lang.isActive)) {
      const firstActive = languages.find((lang) => lang.isActive) || languages[0];
      languages.forEach((lang) => {
        lang.isDefault = lang.code === firstActive.code;
      });
    }
    const nextLanguages = languages.map((lang, displayOrder) => ({ ...lang, displayOrder }));
    updateSettings({
      ...settings,
      defaultLanguage: languages.find((lang) => lang.isDefault)?.code || languages[0].code,
      languages: nextLanguages,
      showSwitcher: nextLanguages.filter((lang) => lang.isActive).length >= 2,
    });
  };

  const addLanguage = (item: StorefrontLanguageItem) => {
    if (settings.languages.some((lang) => lang.code === item.code)) return;
    const nextItem: StorefrontLanguageItem = {
      ...item,
      isActive: true,
      isDefault: settings.languages.length === 0,
      displayOrder: settings.languages.length,
    };
    const languages = [...settings.languages, nextItem];
    updateSettings({
      ...settings,
      languages,
      showSwitcher: languages.filter((lang) => lang.isActive).length >= 2,
    });
  };

  const handleAddCustom = () => {
    const code = custom.code.trim().toLowerCase();
    if (!code || settings.languages.some((lang) => lang.code === code)) {
      setFeedback({ type: 'error', message: t('ecommerceLanguages.customCodeError') });
      return;
    }
    addLanguage({
      code,
      name: custom.name.trim() || code.toUpperCase(),
      nativeName: custom.nativeName.trim() || custom.name.trim() || code,
      flag: custom.flag.trim() || '🏳️',
      isActive: true,
      isDefault: false,
      isRtl: custom.isRtl,
      displayOrder: settings.languages.length,
    });
    setCustom(emptyCustom);
  };

  // Manuel çeviri hedef dilleri: varsayılan dışındaki tüm diller (aktif olması gerekmez).
  const overrideTargetLangs = useMemo(
    () => settings.languages.filter((lang) => lang.code !== settings.defaultLanguage),
    [settings.languages, settings.defaultLanguage]
  );

  useEffect(() => {
    const loadOverrides = async () => {
      if (!companyId) return;
      try {
        const response = await getTranslationOverrides(companyId);
        const data = response?.data || {};
        const rows: OverrideRow[] = Object.entries(data).map(([source, values], idx) => ({
          id: `row-${idx}-${Date.now()}`,
          source,
          values: (values as Record<string, string>) || {},
        }));
        setOverrideRows(rows);
        overridesLoadedRef.current = true;
      } catch (error) {
        console.error('Manuel çeviriler yüklenemedi:', error);
      }
    };
    loadOverrides();
  }, [companyId]);

  const buildOverridesPayload = (rows: OverrideRow[]): Record<string, Record<string, string>> => {
    const payload: Record<string, Record<string, string>> = {};
    rows.forEach((row) => {
      const source = row.source.trim();
      if (!source) return;
      const values: Record<string, string> = {};
      Object.entries(row.values).forEach(([lang, value]) => {
        if (value && value.trim()) values[lang] = value;
      });
      if (Object.keys(values).length > 0) payload[source] = values;
    });
    return payload;
  };

  const persistOverrides = async (rows: OverrideRow[]) => {
    if (!companyId) return;
    setOverridesSaving(true);
    try {
      await saveTranslationOverrides(buildOverridesPayload(rows), companyId);
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
    } catch (error) {
      console.error('Manuel çeviriler kaydedilemedi:', error);
      setFeedback({ type: 'error', message: t('ecommerceLanguages.saveError') });
    } finally {
      setOverridesSaving(false);
    }
  };

  const scheduleOverridesPersist = (rows: OverrideRow[]) => {
    if (!overridesLoadedRef.current || !companyId) return;
    if (overridesSaveTimerRef.current) clearTimeout(overridesSaveTimerRef.current);
    overridesSaveTimerRef.current = setTimeout(() => persistOverrides(rows), 600);
  };

  const updateOverrideRows = (rows: OverrideRow[]) => {
    setOverrideRows(rows);
    scheduleOverridesPersist(rows);
  };

  const handleAddOverrideRow = () => {
    const source = newSource.trim();
    if (!source) return;
    if (overrideRows.some((row) => row.source.trim() === source)) {
      setNewSource('');
      return;
    }
    updateOverrideRows([...overrideRows, { id: `row-${Date.now()}`, source, values: {} }]);
    setNewSource('');
  };

  const handleOverrideSourceChange = (id: string, value: string) => {
    updateOverrideRows(overrideRows.map((row) => (row.id === id ? { ...row, source: value } : row)));
  };

  const handleOverrideValueChange = (id: string, lang: string, value: string) => {
    updateOverrideRows(
      overrideRows.map((row) =>
        row.id === id ? { ...row, values: { ...row.values, [lang]: value } } : row
      )
    );
  };

  const handleRemoveOverrideRow = (id: string) => {
    updateOverrideRows(overrideRows.filter((row) => row.id !== id));
  };

  const handlePrefillRow = async (row: OverrideRow) => {
    const source = row.source.trim();
    if (!source || !companyId) return;
    const targets = overrideTargetLangs.filter((lang) => !row.values[lang.code]?.trim());
    if (targets.length === 0) return;
    setPrefillingId(row.id);
    try {
      const nextValues = { ...row.values };
      for (const lang of targets) {
        const [translated] = await machineTranslateTexts(lang.code, [source], companyId);
        if (translated && translated !== source) nextValues[lang.code] = translated;
      }
      updateOverrideRows(
        overrideRows.map((r) => (r.id === row.id ? { ...r, values: nextValues } : r))
      );
    } catch (error) {
      console.error('Ön doldurma hatası:', error);
    } finally {
      setPrefillingId(null);
    }
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LanguageIcon color="primary" sx={{ mr: 1.5 }} />
          <Typography variant="h6">{t('ecommerceLanguages.title')}</Typography>
          {saving && <CircularProgress size={18} sx={{ ml: 1.5 }} />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('ecommerceLanguages.help')}
        </Typography>

        {feedback && (
          <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showSwitcher}
                  onChange={(e) => updateSettings({ ...settings, showSwitcher: e.target.checked })}
                  disabled={!companyId || activeLanguages.length < 2}
                />
              }
              label={t('ecommerceLanguages.showSwitcher')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {activeLanguages.length < 2
                ? t('ecommerceLanguages.needTwoLanguages')
                : t('ecommerceLanguages.showSwitcherHelp')}
            </Typography>

            <FormControl size="small" sx={{ minWidth: 240, mb: 3 }}>
              <InputLabel>{t('ecommerceLanguages.defaultLanguage')}</InputLabel>
              <Select
                value={settings.defaultLanguage}
                label={t('ecommerceLanguages.defaultLanguage')}
                onChange={(e) => handleSetDefault(String(e.target.value))}
              >
                {activeLanguages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name} ({lang.nativeName})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('ecommerceLanguages.listTitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {settings.languages.map((lang, index) => (
                <Paper
                  key={lang.code}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    bgcolor: lang.isActive ? 'background.paper' : 'action.hover',
                  }}
                >
                  <Typography sx={{ fontSize: 22, lineHeight: 1 }}>{lang.flag}</Typography>
                  <Box sx={{ minWidth: 180, flex: 1 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {lang.name}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {lang.code.toUpperCase()} · {lang.nativeName}
                      </Typography>
                    </Typography>
                  </Box>
                  {lang.isDefault && (
                    <Chip size="small" color="primary" icon={<StarIcon />} label={t('ecommerceLanguages.defaultBadge')} />
                  )}
                  {lang.isRtl && (
                    <Chip size="small" variant="outlined" label={t('ecommerceLanguages.rtlBadge')} />
                  )}
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={lang.isActive}
                        onChange={(e) => handleToggleActive(lang.code, e.target.checked)}
                      />
                    }
                    label={lang.isActive ? t('ecommerceLanguages.active') : t('ecommerceLanguages.inactive')}
                  />
                  <Button
                    size="small"
                    disabled={lang.isDefault}
                    onClick={() => handleSetDefault(lang.code)}
                  >
                    {t('ecommerceLanguages.makeDefault')}
                  </Button>
                  <Tooltip title={t('ecommerceLanguages.moveUp')}>
                    <span>
                      <IconButton size="small" disabled={index === 0} onClick={() => handleMove(index, -1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('ecommerceLanguages.moveDown')}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={index === settings.languages.length - 1}
                        onClick={() => handleMove(index, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('ecommerceLanguages.remove')}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={settings.languages.length <= 1}
                        onClick={() => handleRemove(lang.code)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Paper>
              ))}
            </Box>
          </>
        )}
      </Paper>

      {!loading && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('ecommerceLanguages.addTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceLanguages.addHelp')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {unusedCatalog.map((item) => (
              <Chip
                key={item.code}
                clickable
                icon={<AddIcon />}
                label={`${item.flag} ${item.name}`}
                onClick={() => addLanguage(item)}
              />
            ))}
            {unusedCatalog.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('ecommerceLanguages.catalogEmpty')}
              </Typography>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {t('ecommerceLanguages.customTitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              label={t('ecommerceLanguages.customCode')}
              value={custom.code}
              onChange={(e) => setCustom({ ...custom, code: e.target.value })}
              sx={{ width: 110 }}
            />
            <TextField
              size="small"
              label={t('ecommerceLanguages.customName')}
              value={custom.name}
              onChange={(e) => setCustom({ ...custom, name: e.target.value })}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label={t('ecommerceLanguages.customNative')}
              value={custom.nativeName}
              onChange={(e) => setCustom({ ...custom, nativeName: e.target.value })}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label={t('ecommerceLanguages.customFlag')}
              value={custom.flag}
              onChange={(e) => setCustom({ ...custom, flag: e.target.value })}
              sx={{ width: 90 }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={custom.isRtl}
                  onChange={(e) => setCustom({ ...custom, isRtl: e.target.checked })}
                />
              }
              label={t('ecommerceLanguages.rtlBadge')}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCustom}>
              {t('ecommerceLanguages.addCustom')}
            </Button>
          </Box>
        </Paper>
      )}

      {!loading && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TranslateIcon color="primary" sx={{ mr: 1.5 }} />
            <Typography variant="h6">Manuel Çeviriler</Typography>
            {overridesSaving && <CircularProgress size={18} sx={{ ml: 1.5 }} />}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Otomatik (makine) çevirisini beğenmediğiniz metinler için buradan dile göre elle çeviri
            girin. Buradaki çeviriler vitrinde her zaman önceliklidir. Boş bıraktığınız diller
            otomatik çevrilmeye devam eder.
          </Typography>

          {overrideTargetLangs.length === 0 ? (
            <Alert severity="info">
              Manuel çeviri girmek için varsayılan dil dışında en az bir dil ekleyin.
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Kaynak metin (Türkçe)"
                  value={newSource}
                  onChange={(e) => setNewSource(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddOverrideRow();
                  }}
                  sx={{ flex: 1, minWidth: 240 }}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddOverrideRow}>
                  Ekle
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {overrideRows.map((row) => (
                  <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <TextField
                        size="small"
                        label="Kaynak metin (Türkçe)"
                        value={row.source}
                        onChange={(e) => handleOverrideSourceChange(row.id, e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <Tooltip title="Boş dilleri makine çevirisiyle doldur">
                        <span>
                          <IconButton
                            color="primary"
                            onClick={() => handlePrefillRow(row)}
                            disabled={prefillingId === row.id || !row.source.trim()}
                          >
                            {prefillingId === row.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <AutoFixHighIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Satırı sil">
                        <span>
                          <IconButton color="error" onClick={() => handleRemoveOverrideRow(row.id)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {overrideTargetLangs.map((lang) => (
                        <TextField
                          key={lang.code}
                          size="small"
                          label={`${lang.flag} ${lang.name}`}
                          value={row.values[lang.code] || ''}
                          onChange={(e) => handleOverrideValueChange(row.id, lang.code, e.target.value)}
                          sx={{ flex: '1 1 220px', minWidth: 180 }}
                        />
                      ))}
                    </Box>
                  </Paper>
                ))}
                {overrideRows.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Henüz manuel çeviri eklenmedi. Yukarıdan bir kaynak metin ekleyin.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default LanguageSettings;
